import { importContactsLineNumberLabel } from './encodingUtils.locstring.json';
import loc from 'owa-localize';
import transpose from 'lodash-es/zip';
import { getLogger, sum } from '../../shared';
import { LazyImport, LazyModule } from 'owa-bundling';

const textEncodingLazyModule = new LazyModule(() => import('text-encoding'));
const textDecoder = new LazyImport(textEncodingLazyModule, m => m.TextDecoder);
const papaparseLazyModule = new LazyModule(() => import('papaparse'));

const bom = [0xef, 0xbb, 0xbf]; // Byte Order Mark (BOM) for UTF-8

// Matching all non-ASCII characters except the non-breaking white space. Also treats two
// or more "question marks only" strings as non-ASCII, as incorrectly encoded non-ASCII
// characters sometimes end up as normal ASCII question marks.
const nonAsciiRegEx = /[^\u0000-\u007E\u00A0]|^\?{2,}$/g;

const numberOfExampleLabels = 3;
const numberOfExampleContacts = 7;

type TopValuesType = { idx: number; count: number }[];

export interface PapaParse {
    parse: (csvString: string, config?: Papa.ParseConfig) => Papa.ParseResult;
}

/**
 * Adds BOM to the bytes array if not alredy there
 * @param bytes A byte array to check
 */
export function addUtf8Bom(bytes: number[]): number[] {
    return bytes ? (hasUtf8Bom(bytes) ? bytes : bom.concat(bytes)) : bom;
}

/**
 * Returns true if the byte array contains the UTF-8 BOM
 * @param bytes A byte array to check
 */
export function hasUtf8Bom(bytes: number[]): boolean {
    return (
        bytes &&
        bytes.length >= 3 &&
        bytes[0] === bom[0] &&
        bytes[1] === bom[1] &&
        bytes[2] === bom[2]
    );
}

export function getEncodingExamples(csvBytes: Uint8Array): Promise<string[][]> {
    return getTextDecoder().then((textDecoder: typeof TextEncoding.TextDecoder) => {
        const decoderUtf8 = new textDecoder('utf-8');
        return decoderUtf8 && getEncodingExamplesInternalParse(decoderUtf8.decode(csvBytes));
    });
}

function getEncodingExamplesInternalParse(csvInUtf8: string): Promise<string[][]> {
    return getPapaparse().then((papaparse: PapaParse) => {
        const parseConfig: Papa.ParseConfig = {
            delimiter: ',',
            header: false,
            skipEmptyLines: true,
        };

        const csvMap = papaparse.parse(csvInUtf8, parseConfig);
        return getEncodingExamplesInternal(csvMap);
    });
}

/**
 * Returns a table of property headers and values for contacts, where the number
 * of non-ASCII characters in the property values is used as a selection criteria.
 * @param csvMap A map containing the UTF-8 represenation of the complete CSV content
 */
function getEncodingExamplesInternal(csvMap: Papa.ParseResult): string[][] {
    try {
        // Remove and save the header labels (property names) for later
        const orgLabels: string[] = csvMap.data.shift();

        // Calculate the count of non-ASCII characters for each property.
        // Table is organized with contacts in rows and labels in columns.
        // Non-ASCII characters are matched individuall, while question marks are matches
        // as a group, so the total value is calculated as the sum of the length of all matches.
        const rowsNonAsciiCount: number[][] = csvMap.data.map(contact =>
            contact.map(property => {
                const matches: string[] = (property && property.match(nonAsciiRegEx)) || [];
                return sum(matches.map(value => value.length));
            })
        );

        // The count of non-ASCII characters per property.
        // Table is organized with contacts in columns and labels in rows. Properties wihtout label gets value 0
        const colsNonAsciiCount: number[][] = transpose(...rowsNonAsciiCount)
            .slice(0, orgLabels.length)
            .map(label => label.map(count => count || 0));

        // The labels with largest total count of non-ASCII characters for all contacts.
        const topLabelsMap: TopValuesType = getTopValues(colsNonAsciiCount, numberOfExampleLabels);

        // The top labels (properties) where most non-ASCII characters are found for all contacts.
        let topLabels: string[] = topLabelsMap.map(top => orgLabels[top.idx]);

        if (topLabels.length === 0) {
            return null;
        }

        // The total count of non-ASCII characters per contact for the calculated top labels.
        // Table is organized with contacts in rows and labels in columns.
        const rowsNonAsciiCountForExampleLabels: number[][] = transpose(
            ...topLabelsMap.map(x => colsNonAsciiCount[x.idx])
        );

        // The contacts with largest total count of non-ASCII characters
        const topContactsMap: TopValuesType = getTopValues(
            rowsNonAsciiCountForExampleLabels,
            numberOfExampleContacts
        );

        // The top contacts with the largest total count of non-ASCII characters from the calculated top labels.
        const topContacts: string[][] = topContactsMap
            .map(top => csvMap.data[top.idx])
            .map(contact => topLabelsMap.map(top => contact[top.idx]))
            .map(contact => contact.map(value => value || ''));

        // In case a top label doesn't have any non-ASCII characters for the top contacts, we remove
        // the label to avoid showing an empty or non-interesting column in the UI
        const labelHasNonAsciiProperty: boolean[] = [];
        topContacts.forEach(contact => {
            contact.forEach((value, idx) => {
                const matches = value.match(nonAsciiRegEx) || [];
                labelHasNonAsciiProperty[idx] =
                    labelHasNonAsciiProperty[idx] || sum(matches.map(value => value.length)) > 0;
            });
        });

        // Remove the labels (columns) that don't have any values
        const trimmedTopLabels = topLabels.filter((label, idx) => labelHasNonAsciiProperty[idx]);
        const trimmedTopContacts = topContacts.map(contact =>
            contact.filter((label, idx) => labelHasNonAsciiProperty[idx])
        );

        // Combined table for top labels and contacts. Table is organized with contacts in columns and labels in rows.
        const colTop: string[][] = transpose(...[trimmedTopLabels].concat(trimmedTopContacts));

        // Arrange the combined top table based on the order of the original labels in the CSV file
        const colArrangedTop: string[][] = [];
        orgLabels.forEach(orgLabel => {
            const idx = colTop.findIndex(row => row[0] === orgLabel);
            if (idx !== -1) {
                colArrangedTop.push(colTop[idx]);
            }
        });

        // The line number in the CSV file for the top contacts. Need to add 2 since we want to
        // start counting on 1, and also need to include the header.
        const topLineNumbers: string[] = topContactsMap.map(top => (top.idx + 2).toString());

        // Add line numbers from CSV file to the table
        colArrangedTop.unshift([loc(importContactsLineNumberLabel)].concat(topLineNumbers));

        const rowArrangedTop = transpose(...colArrangedTop);

        // Return the table with contacts sorted on the line numbers from the CSV file
        topLabels = rowArrangedTop.shift();

        // The first element in rowArrangedTop holds the line number, which we sort on
        return [topLabels].concat(rowArrangedTop.sort((a, b) => Number(a[0]) - Number(b[0])));
    } catch (error) {
        getLogger().log('GetEncodingExamplesFailed', {
            component: 'importContacts',
            error: error ? error.message : undefined,
        });

        return [];
    }
}

/**
 * Returns true if there are any encoding examples, false if not.
 * @param examples Table holding the encoding examples
 */
export const hasEncodingExamples = (examples: string[][]): boolean => {
    return examples && examples.length > 0 && examples[0].length > 0;
};

/**
 * Returns the entries with the highest value, sorted descending. Entries with count 0 are skipped.
 * The returned entries contain both the value and the original array index of the entry.
 * @param values The values to iterate over
 * @param limit The max number of entries to return
 */
function getTopValues(values: number[][], limit: number): TopValuesType {
    return values
        .map(sum)
        .map((count, idx) => {
            return { idx, count };
        })
        .sort((a, b) => b.count - a.count)
        .filter(x => x.count !== 0)
        .slice(0, limit);
}

export function getTextDecoder(): Promise<{}> {
    return textDecoder.import();
}

export function getPapaparse(): Promise<{}> {
    return papaparseLazyModule.import();
}
